/**
 * Context Detector
 * 
 * Detects browser context and storage capabilities.
 * Used to select the best available storage adapter.
 */

// <reference path="../../../types/iframe-resizer.d.ts" />

import type { BrowserContext, StorageCapabilities } from '../types';
import { STORAGE_KEYS, TIMEOUTS, MESSAGE_TYPES } from '../constants';

// ============================================================================
// Browser Context Detection
// ============================================================================

/**
 * Detect if running inside an iframe
 * 
 * @returns true if in iframe, false if standalone
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin error = definitely in iframe
    return true;
  }
}

/**
 * Detect if running in third-party context
 * 
 * Third-party contexts have restricted storage access:
 * - Cross-origin iframes
 * - Safari private browsing
 * - Firefox strict tracking protection
 * 
 * @returns true if storage may be blocked
 */
export function isThirdPartyContext(): boolean {
  // Quick check: try to use localStorage
  try {
    localStorage.setItem(STORAGE_KEYS.TEST_KEY, 'test');
    localStorage.removeItem(STORAGE_KEYS.TEST_KEY);
    return false;
  } catch {
    return true;
  }
}

/**
 * Check if iframe-resizer is available
 * 
 * @returns true if window.parentIFrame exists
 */
export function hasIframeResizer(): boolean {
  return typeof window.parentIFrame !== 'undefined';
}

/**
 * Get browser context information
 * 
 * @returns Browser context details
 */
export function detectBrowserContext(): BrowserContext {
  const iframe = isInIframe();
  const thirdParty = isThirdPartyContext();
  const resizer = hasIframeResizer();

  return {
    isIframe: iframe,
    isThirdPartyContext: thirdParty,
    hasIframeResizer: resizer,
    isStandalone: !iframe,
  };
}

// ============================================================================
// Storage Capability Detection
// ============================================================================

/**
 * Test if localStorage is available and working
 * 
 * @returns true if localStorage works
 */
export async function testLocalStorage(): Promise<boolean> {
  try {
    const testKey = STORAGE_KEYS.TEST_KEY;
    const testValue = 'test';
    
    localStorage.setItem(testKey, testValue);
    const retrieved = localStorage.getItem(testKey);
    localStorage.removeItem(testKey);
    
    return retrieved === testValue;
  } catch {
    return false;
  }
}

/**
 * Test if sessionStorage is available and working
 * 
 * @returns true if sessionStorage works
 */
export async function testSessionStorage(): Promise<boolean> {
  try {
    const testKey = STORAGE_KEYS.TEST_KEY;
    const testValue = 'test';
    
    sessionStorage.setItem(testKey, testValue);
    const retrieved = sessionStorage.getItem(testKey);
    sessionStorage.removeItem(testKey);
    
    return retrieved === testValue;
  } catch {
    return false;
  }
}

/**
 * Test if IndexedDB is available and working
 * 
 * @returns true if IndexedDB works
 */
export async function testIndexedDB(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') {
    return false;
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), TIMEOUTS.INDEXEDDB_MS);

    try {
      const testDBName = '__test_indexeddb__';
      const request = indexedDB.open(testDBName);

      request.onsuccess = () => {
        clearTimeout(timeout);
        request.result.close();
        indexedDB.deleteDatabase(testDBName);
        resolve(true);
      };

      request.onerror = () => {
        clearTimeout(timeout);
        resolve(false);
      };

      request.onblocked = () => {
        clearTimeout(timeout);
        resolve(false);
      };
    } catch {
      clearTimeout(timeout);
      resolve(false);
    }
  });
}

/**
 * Test if parent bridge is available
 * 
 * Checks if:
 * 1. Running in iframe
 * 2. iframe-resizer is loaded
 * 3. Parent window is accessible
 * 
 * @returns true if parent bridge can be used
 */
export async function testParentBridge(): Promise<boolean> {
  if (!isInIframe()) {
    return false;
  }

  if (!hasIframeResizer()) {
    return false;
  }

  const parentIFrame = window.parentIFrame;
  if (!parentIFrame) {
    return false;
  }

  // Test if parent responds to messages
  return new Promise((resolve) => {
    // Capture original handler before modifying
    const originalOnMessage = window.iFrameResizer?.onMessage;
    
    // Helper to restore original handler and resolve
    const cleanup = (result: boolean) => {
      if (window.iFrameResizer) {
        window.iFrameResizer.onMessage = originalOnMessage;
      }
      resolve(result);
    };

    const timeout = setTimeout(() => cleanup(false), TIMEOUTS.PARENT_BRIDGE_MS);

    try {
      const testId = `test_${Date.now()}`;
      
      // Send test message
      parentIFrame.sendMessage({
        type: MESSAGE_TYPES.STORAGE_TEST,
        id: testId,
      });

      // Listen for response
      window.iFrameResizer = {
        ...window.iFrameResizer,
        onMessage: (message: unknown) => {
          // Type guard for response
          const isResponse = (msg: unknown): msg is { type: string; id: string } => {
            return (
              typeof msg === 'object' &&
              msg !== null &&
              'type' in msg &&
              'id' in msg
            );
          };

          if (isResponse(message) && message.type === 'storage.response' && message.id === testId) {
            clearTimeout(timeout);
            cleanup(true);
          } else if (originalOnMessage) {
            // Pass through to original handler
            originalOnMessage(message);
          }
        },
      };
    } catch {
      clearTimeout(timeout);
      cleanup(false);
    }
  });
}

/**
 * Test if cookies are available
 * 
 * @returns true if cookies work
 */
export async function testCookies(): Promise<boolean> {
  try {
    const testKey = STORAGE_KEYS.TEST_KEY;
    const testValue = 'test';
    
    document.cookie = `${testKey}=${testValue}; path=/; SameSite=Lax`;
    const retrieved = document.cookie.includes(`${testKey}=${testValue}`);
    
    // Clean up
    document.cookie = `${testKey}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    
    return retrieved;
  } catch {
    return false;
  }
}

/**
 * Detect all storage capabilities
 * 
 * Runs tests in parallel for performance.
 * 
 * @returns Storage capabilities object
 */
export async function detectStorageCapabilities(): Promise<StorageCapabilities> {
  // Test all capabilities in parallel
  const [
    hasLocalStorage,
    hasSessionStorage,
    hasIndexedDB,
    hasParentBridge,
    hasCookies,
  ] = await Promise.all([
    testLocalStorage(),
    testSessionStorage(),
    testIndexedDB(),
    testParentBridge(),
    testCookies(),
  ]);

  return {
    localStorage: hasLocalStorage,
    sessionStorage: hasSessionStorage,
    indexedDB: hasIndexedDB,
    parentBridge: hasParentBridge,
    cookies: hasCookies,
  };
}

/**
 * Log context and capabilities (dev mode only)
 * 
 * @param context Browser context
 * @param capabilities Storage capabilities
 */
export function logCapabilities(
  _context: BrowserContext,
  _capabilities: StorageCapabilities
): void {
  if (!import.meta.env.DEV) return;

  // console.group('[BrowserAdapter] 🔍 Context Detection');
  // console.log('Context:', {
  //   iframe: context.isIframe,
  //   thirdParty: context.isThirdPartyContext,
  //   resizer: context.hasIframeResizer,
  //   standalone: context.isStandalone,
  // });
  // console.log('Capabilities:', capabilities);
  // console.groupEnd();
}
